import type { Content } from '@prismicio/client';

/**
 * The variations of this slice that render an interactive resource library
 * (filters, search, sort, load more). All share the hook/controls in this
 * folder and differ only in the card layout and outer grid.
 */
export const RESOURCE_LIBRARY_VARIATIONS = [
  'default',
  'horizontalCards',
] as const;

export type ResourceLibraryVariation =
  (typeof RESOURCE_LIBRARY_VARIATIONS)[number];

export type ResourceLibrarySlice = Extract<
  Content.ResourceLibraryGridSlice,
  { variation: ResourceLibraryVariation }
>;

export interface PostData {
  uid: string;
  id: string;
  title: string;
  description: string;
  image: string | null;
  imageAlt: string;
  date: string | null;
  categoryUid: string | null;
  categoryName: string | null;
  author: { name: string; avatar: string | null; uid: string } | null;
  readDuration: number | null;
  featured: boolean;
  type: 'insights' | 'ebooks' | 'webinars';
  href: string | null;
}

export interface ApiResponse {
  posts: PostData[];
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export type SortOption = 'relevant' | 'oldest' | 'newest';

export const CONTENT_TYPES = [
  { id: 'insights', label: 'Insights' },
  { id: 'ebooks', label: 'Ebooks' },
  { id: 'webinars', label: 'Webinars' },
] as const;

export const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'relevant', label: 'More relevant' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'newest', label: 'Newest' },
];

export const PAGE_SIZE = 9;
export const MIN_SEARCH_LENGTH = 4;

export function getTypeBadgeLabel(type: PostData['type']): string {
  switch (type) {
    case 'ebooks':
      return 'EBOOK';
    case 'webinars':
      return 'WEBINARS';
    case 'insights':
    default:
      return 'INSIGHTS';
  }
}

export function getCtaLabel(type: PostData['type']): string {
  switch (type) {
    case 'ebooks':
      return 'Get Ebook';
    case 'webinars':
      return 'Access webinar';
    case 'insights':
    default:
      return 'Read more';
  }
}

export function getPostHref(post: PostData): string {
  if (post.type === 'ebooks') {
    return post.href || `/ebooks/${post.uid}`;
  }
  if (post.type === 'webinars') {
    return post.href || `/resources-center/webinar/${post.uid}`;
  }
  return post.href || `/resources-center/${post.uid}`;
}
